import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from '@shared/services/toastr.service';
import { HostModeService } from '@shared/services/hostMode.service';
import { EXTERNAL_RFC_7234_CACHE_LINK } from 'app/app-routing.constants';
import { combineLatestWith, firstValueFrom, from, Observable } from 'rxjs';
import { OrganizationSiteService } from '@shared/services/organization-site.service';
import { Site } from '@shared/models/site';
import { ConfirmService } from '@shared/services/confirm.service';
import { isApex } from '@shared/utils/site';
import { NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import { HelpComponent } from '@shared/components/help/help.component';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { FormsModule } from '@angular/forms';

const PURGE_REGEXP = 'purge-regexp';
const ORANGE = 'ORANGE';

@Component({
  selector: 'app-cache-cdn',
  templateUrl: './cache-cdn.component.html',
  styleUrls: ['./cache-cdn.component.scss', '../../my-sites/site-status.scss', '../add-site.component.scss'],
  imports: [NgIf, HelpComponent, NgStyle, SwitchComponent, FormsModule, NgFor, NgClass, TranslatePipe],
})
export class CacheCdnComponent implements OnInit {
  @Input({ required: true }) siteName: string;
  @Input({ required: true }) showCdn: boolean;
  @Output() cacheEnabledChange = new EventEmitter<boolean>();

  ruleUseCache: boolean;

  EXTERNAL_RFC_7234_CACHE_LINK = EXTERNAL_RFC_7234_CACHE_LINK;
  orangeCdb =
    '<span class="orange-cdb"><span class="cdb">Content Delivery Boost</span> by <img src="/assets/images/orange.svg" aria-label="orange"></span>';

  site: Site;
  disabled = true;
  purgeRegexp = '';
  cacheEnabled: boolean;
  cdnEnabled: boolean;
  hasActiveCertificate: boolean;
  cdnNotAvailableReason: string[];

  constructor(
    private readonly siteApi: OrganizationSiteService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    public readonly hostModeService: HostModeService,
    private readonly confirmService: ConfirmService,
  ) {}

  ngOnInit(): void {
    this.purgeRegexp = localStorage.getItem(PURGE_REGEXP);
    this.siteApi
      .getSite(this.siteName)
      .pipe(combineLatestWith(this.siteApi.getCertificates(this.siteName), this.siteApi.getRules(this.siteName)))
      .subscribe({
        next: ([site, certificates, rules]) => {
          this.hasActiveCertificate = certificates.some((cert) => cert.active);
          this.ruleUseCache = rules.some((rule) => rule.cache);
          this.setSite(site);
        },
        error: (error) => {
          this.toastr.error(`${error}`, 'Failed to load site');
        },
      });
  }

  setSite(site: Site) {
    this.site = site;
    this.disabled = !this.hostModeService.isAdvanced(this.site.mode);
    this.cacheEnabled = site.cacheEnabled;
    this.cdnEnabled = !!site.cdn;
    this.cdnNotAvailableReason = [];
    if (!this.cacheEnabled) this.cdnNotAvailableReason.push('CDN.cannotEnable.Cache is not enabled');
    if (isApex(site.domainName)) this.cdnNotAvailableReason.push('CDN.cannotEnable.Site is APEX');
    if (!this.hasActiveCertificate)
      this.cdnNotAvailableReason.push('CDN.cannotEnable.Site does not have an active certificate');
    if (this.site.tlsOptions?.clientAuthType) this.cdnNotAvailableReason.push('CDN.cannotEnable.Site has mTLS');
  }

  toggleCache = (): Observable<boolean> => {
    if (this.disabled) return from([false]);

    const checked = !this.cacheEnabled;

    if (!checked && this.ruleUseCache) {
      this.toastr.error('RuleUseCache');
      return from([false]);
    }

    return this.confirmService.confirmPipe(
      {
        title: this.translate.instant('Confirmation'),
        html: this.translate.instant(checked ? 'EnableCache' : 'DisableCache'),
      },
      () =>
        this.siteApi.patchSite(this.site.domainName, { cacheEnabled: checked }).subscribe((updatedSite) => {
          this.setSite(updatedSite);
          this.cacheEnabledChange.emit(updatedSite.cacheEnabled);
          this.toastr.success();
        }),
    );
  };

  toggleCdn = () => {
    const checked = !this.cdnEnabled;

    return this.confirmService.confirmPipe(
      {
        title: this.translate.instant('Confirmation'),
        html: this.translate.instant(checked ? 'CDN.confirmation.enable' : 'CDN.confirmation.disable'),
      },
      () =>
        this.siteApi.patchSite(this.site.domainName, { cdn: checked ? ORANGE : null }).subscribe((updatedSite) => {
          this.setSite(updatedSite);
          this.toastr.success();
        }),
    );
  };

  async purge() {
    localStorage.setItem(PURGE_REGEXP, this.purgeRegexp);
    this.siteApi.purgeCache(this.site.domainName, this.purgeRegexp).subscribe(() => this.toastr.success());
  }
}
